import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Form, Button, Pagination } from 'react-bootstrap';
import APIserver from '../../../../api/axiosConfig';
import Swal from 'sweetalert2';
import { Link } from 'react-router-dom';

const ReporteGastos = () => {
    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaFin, setFechaFin] = useState('');
    const [totalEgresos, setTotalEgresos] = useState({ numeroPagos: 0, totalEgresos: 0 });
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0); // Estado para el total de registros
    const [data, setData] = useState([]); // Datos de los bancos
    const [pagos, setPagos] = useState([]); // Estado para almacenar los pagos recibidos

    useEffect(() => {
        const today = new Date();

        // Fecha de inicio del mes (primer día)
        const primerDiaMes = new Date(today.getFullYear(), today.getMonth(), 1);
        const fechaInicioMes = primerDiaMes.toISOString().split('T')[0]; // Formato YYYY-MM-DD

        // Fecha de fin del mes (último día)
        const ultimoDiaMes = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        const fechaFinMes = ultimoDiaMes.toISOString().split('T')[0];

        setFechaInicio(fechaInicioMes);
        setFechaFin(fechaFinMes);
    }, []);

    const manejarExportar = async () => {
        try {
            const response = await APIserver.get('/api/reportesInventario/exportarPorFecha', {
                params: { fechaInicio, fechaFin },
                responseType: 'blob', // Para manejar el archivo como binario
            });

            // Verificar si la respuesta está vacía
            if (response.status === 204 || response.data.size === 0) { // status 204 o archivo vacío
                Swal.fire({
                    title: 'Sin Productos',
                    text: 'No hay productos disponibles en el rango de fechas seleccionado.',
                    icon: 'info',
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true,
                    iconColor: '#2fb380',
                    confirmButtonColor: "#2c4cc4"
                });
                return; // Detener el flujo
            }

            // Crear enlace para descarga
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Productos_Filtrados.xlsx');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (error) {
            if (error.response && error.response.status === 404) {
                Swal.fire({
                    title: 'Sin Productos',
                    text: 'No hay productos disponibles en el rango de fechas seleccionado.',
                    icon: 'info',
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true,
                    iconColor: '#2fb380',
                    confirmButtonColor: "#2c4cc4"
                });
            } else {
                console.error('Error al exportar productos:', error);
                Swal.fire({
                    title: 'Error',
                    text: 'Ocurrió un error inesperado al exportar los productos.',
                    icon: 'error',
                    confirmButtonColor: "#2c4cc4"
                });
            }
        }
    };

    // Función para formatear valores en COP
    const formatCOP = (value) => value?.toLocaleString('es-CO', {
        style: 'currency',
        currency: 'COP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }) || '$0';

    const fetchReportes = async () => {
        try {
            // Realizamos la solicitud al servidor
            const totalEgresosRes = await APIserver.get('/api/reportesInventario/total-egresos', {
                params: { fechaInicio, fechaFin }, // Pasamos las fechas como parámetros de consulta
            });


            // Validamos y actualizamos el estado con los datos obtenidos
            if (totalEgresosRes.data) {
                setTotalEgresos(totalEgresosRes.data);
            } else {
                setTotalEgresos({ numeroPagos: 0, totalegresos: 0 });
            }
        } catch (error) {
            console.error("Error fetching report data:", error);
        }
    };

    useEffect(() => {
        if (fechaInicio && fechaFin) { // Asegúrate de que ambas fechas estén seleccionadas
            fetchReportes();
        }
    }, [fechaInicio, fechaFin]); // Cierre correcto del array de dependencias

    const fetchData = async (page = 1, limit = 10) => {
        try {
            const response = await APIserver.get('api/reportesInventario/total-egresos-bancos', {
                params: {
                    fechaInicio,
                    fechaFin,
                    page,
                    limit,
                },
            });

            setData(response.data.data); // Los datos de los bancos
            setTotalRecords(response.data.totalCount); // El total de registros
            setTotalPages(Math.ceil(response.data.totalCount / limit)); // Calculamos las páginas totales
        } catch (error) {
            console.error('Error al obtener los datos:', error);
        }
    };

    // Cargar datos al montar el componente o cambiar la paginación
    useEffect(() => {
        if (fechaInicio && fechaFin) { // Asegúrate de que ambas fechas estén seleccionadas

            fetchData(currentPage, rowsPerPage);
        }
    }, [currentPage, rowsPerPage, fechaInicio, fechaFin]);

    const handlePageChange = (page) => {
        setCurrentPage(page); // Actualizar la página actual
        fetchData(page, rowsPerPage); // Cargar los datos correspondientes a esa página
    };

    // Función para renderizar los botones de paginación
    const renderPaginationItems = () => {
        const items = [];
        const pageRangeDisplayed = 5; // Número de páginas visibles
        let startPage = Math.max(1, currentPage - Math.floor(pageRangeDisplayed / 2));
        let endPage = Math.min(totalPages, startPage + pageRangeDisplayed - 1);

        // Ajustamos si el rango de páginas es menor al rango visible
        if (endPage - startPage < pageRangeDisplayed - 1) {
            startPage = Math.max(1, endPage - pageRangeDisplayed + 1);
        }

        // Mostrar los botones de navegación
        if (startPage > 1) {
            items.push(
                <Pagination.First key="first" onClick={() => handlePageChange(1)} />
            );
        }

        if (currentPage > 1) {
            items.push(
                <Pagination.Prev key="prev" onClick={() => handlePageChange(currentPage - 1)} />
            );
        }

        // Mostrar las páginas dentro del rango visible
        for (let number = startPage; number <= endPage; number++) {
            if (number <= totalPages) {
                items.push(
                    <Pagination.Item
                        key={number}
                        active={number === currentPage}
                        onClick={() => handlePageChange(number)}
                    >
                        {number}
                    </Pagination.Item>
                );
            }
        }

        if (currentPage < totalPages) {
            items.push(
                <Pagination.Next key="next" onClick={() => handlePageChange(currentPage + 1)} />
            );
        }

        if (endPage < totalPages) {
            items.push(
                <Pagination.Last key="last" onClick={() => handlePageChange(totalPages)} />
            );
        }

        return items;
    };

    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value));
        setCurrentPage(1);
    };

    useEffect(() => {
        if (fechaInicio && fechaFin) { // Asegúrate de que ambas fechas estén seleccionadas

            fetchPagos();
        }
    }, [rowsPerPage, currentPage, fechaInicio, fechaFin]);

    const fetchPagos = async () => {
        try {
            const response = await APIserver.get(`/api/reportesInventario/total-pagos`, {
                params: {
                    fechaInicio,
                    fechaFin,
                    limit: rowsPerPage,
                    offset: (currentPage - 1) * rowsPerPage,

                }
            });
            const { results, totalPages } = response.data;
            setPagos(results);
            setTotalPages(totalPages); // Usar el totalPages que ahora viene del backend
        } catch (error) {
            console.error('Error al obtener los pagos:', error);
        }
    };

    // Función para formatear la fecha en formato DD/MM/YYYY
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getUTCDate()).padStart(2, '0');
        const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Los meses son de 0 a 11
        const year = date.getUTCFullYear();
        return `${day}/${month}/${year}`;
    };

    return (
        <Container fluid className="py-3">
            {/* Filtros de Reporte */}
            <div className='d-flex flex-column flex-md-row align-items-center align-items-md-end justify-content-center mb-3 text-center'>
                <div className='d-flex flex-column me-md-3 mb-3 mb-md-0'>
                    <Form.Label>Desde</Form.Label>
                    <Form.Control
                        type='date'
                        value={fechaInicio}
                        onChange={(e) => {
                            const selectedDate = e.target.value;
                            if (new Date(selectedDate) <= new Date(fechaFin || new Date())) {
                                setFechaInicio(selectedDate);
                            }
                        }}
                        max={new Date().toISOString().split('T')[0]} // Fecha máxima: Hoy
                        style={{ minWidth: '200px' }}
                    />
                </div>
                <div className='d-flex flex-column me-md-3 mb-3 mb-md-0'>
                    <Form.Label>Hasta</Form.Label>
                    <Form.Control
                        type='date'
                        value={fechaFin}
                        onChange={(e) => {
                            const selectedDate = e.target.value;
                            if (new Date(selectedDate) >= new Date(fechaInicio) && new Date(selectedDate) <= new Date()) {
                                setFechaFin(selectedDate);
                            }
                        }}
                        max={new Date().toISOString().split('T')[0]} // Fecha máxima: Hoy
                        style={{ minWidth: '200px' }}
                    />
                </div>
                <div className='d-flex align-items-center justify-content-end mt-3 mt-md-0'>
                    <Button variant='danger' className='me-2' onClick={manejarExportar}>
                        Exportar
                    </Button>
                </div>
            </div>

            <Row className="mb-4 text-center">
                <Col md={3}>
                    <Card bg='light' className='text-dark'>
                        <Card.Body>
                            <Card.Title>Total Egresos {totalEgresos.numeroPagos || 0}</Card.Title>
                            <h4>{formatCOP(totalEgresos.totalEgresos || 0)}</h4>
                        </Card.Body>
                    </Card>
                </Col>
                <Col>
                    <div style={{ boxShadow: "0px 0px 15px rgba(0, 0, 0, 0.1)" }}>
                        <Table responsive className='text-center align-middle border'>
                            <thead>
                                <tr>
                                    <th colSpan={2}>
                                        <div className='d-flex justify-content-between align-items-center'>
                                            <div>
                                                <Form.Select
                                                    className='ms-2'
                                                    value={rowsPerPage}
                                                    onChange={handleChangeRowsPerPage}
                                                >
                                                    <option value='5'>5</option>
                                                    <option value='10'>10</option>
                                                    <option value='20'>20</option>
                                                    <option value='50'>50</option>
                                                </Form.Select>
                                            </div>
                                        </div>
                                    </th>
                                </tr>
                                <tr>
                                    <th>Banco</th>
                                    <th>Monto</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((row, index) => (
                                    <tr key={index}>
                                        <td>{row.banco}</td>
                                        <td>${row.totalMonto.toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={2}>
                                        <div className='d-flex justify-content-center align-items-center'>
                                            <Pagination>
                                                {renderPaginationItems()}
                                            </Pagination>
                                        </div>
                                    </td>
                                </tr>
                            </tfoot>
                        </Table>
                    </div>
                </Col>
            </Row>

            <div style={{ boxShadow: "0px 0px 15px rgba(0, 0, 0, 0.1)" }}>
                <Table responsive className='text-center align-middle border'>
                    <thead>
                        <tr>
                            <th colSpan={7}>
                                <div className='d-flex justify-content-between align-items-center'>
                                    <div>
                                        <Form.Select
                                            className='ms-2'
                                            value={rowsPerPage}
                                            onChange={handleChangeRowsPerPage}
                                        >
                                            <option value='5'>5</option>
                                            <option value='10'>10</option>
                                            <option value='20'>20</option>
                                            <option value='50'>50</option>
                                        </Form.Select>
                                    </div>
                                </div>
                            </th>
                        </tr>
                        <tr>
                            <th>Pago</th>
                            <th>Factura</th>
                            <th>Proveedor</th>
                            <th>Concepto</th>
                            <th>Creación</th>
                            <th>Cuenta</th>
                            <th>Monto</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pagos.map((pago) => (
                            <tr key={pago.numPagoHe}>
                                <td>
                                    <Link to={`/admin/PagosRealizados/DetallesPago/${pago.idPago}/`} className='text-decoration-none'>
                                        {pago.numPagoHe}
                                    </Link>
                                </td>
                                <td>
                                    <Link to={`/admin/Compras/DetallesCompra/${pago.idCompra}/`} className='text-decoration-none'>
                                        {pago.numCompra}
                                    </Link>
                                </td>
                                <td>
                                    <Link to={`/admin/Proveedores/DetallesProveedores/${pago.idProveedor}/`} className='text-decoration-none'>
                                        {pago.nomProveedor}
                                    </Link>
                                </td>
                                <td>{pago.concepto}</td>
                                <td>{formatDate(pago.fecha)}</td>
                                <td>
                                    <Link to={`/admin/Bancos/DetallesBancos/${pago.idBanco}/`} className='text-decoration-none'>
                                        {pago.nombreBanco}
                                    </Link>
                                </td>
                                <td>{formatCOP(pago.monto)}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan={7}>
                                <div className='d-flex justify-content-center align-items-center'>
                                    <Pagination>
                                        {renderPaginationItems()}
                                    </Pagination>
                                </div>
                            </td>
                        </tr>
                    </tfoot>
                </Table>
            </div>
        </Container>
    );
};

export default ReporteGastos;
